import { MonitorStatusType, MonitorTransientStatusType } from "../Consts";
import { Monitor } from "../model";

// Immutable class
export class MonitorSelector {

  constructor(
    public readonly monitor: Monitor,
    public readonly selected: boolean = false,
    public readonly fetching: boolean = false) {
  }

  delete(): MonitorSelector {
    switch (this.monitor.status) {
      case MonitorStatusType.Up:
      case MonitorStatusType.Degraded:
      case MonitorStatusType.Disrupted:
      case MonitorStatusType.Paused:
      case MonitorStatusType.Maintenance:
      case MonitorStatusType.Down:
      case MonitorStatusType.RequiresPaymentMethod:
        return new MonitorSelector(this.monitor, this.selected, true);
      default:
        throw new Error(`Can't delete the Monitor ${this.monitor.name} with status ${this.monitor.status}`)
    }
  }

  maintain(): MonitorSelector {
    switch (this.monitor.status) {
      case MonitorStatusType.Up:
      case MonitorStatusType.Degraded:
      case MonitorStatusType.Disrupted:
      case MonitorStatusType.Down:
        return new MonitorSelector(this.monitor, this.selected, true);
      default:
        throw new Error(`Can't maintain the Monitor ${this.monitor.name} with status ${this.monitor.status}`)
    }
  }

  resume(): MonitorSelector {
    switch (this.monitor.status) {
      case MonitorStatusType.Maintenance:
        return new MonitorSelector(this.monitor, this.selected, true);
      default:
        throw new Error(`Can't maintain the Monitor ${this.monitor.name} with status ${this.monitor.status}`)
    }
  }

  select(): MonitorSelector {
    if (this.fetching || this.selected) {
      return this;
    }
    return new MonitorSelector(this.monitor, true, this.fetching);
  }
  
  unselect(): MonitorSelector {
    if (this.fetching || !this.selected) {
      return this;
    }
    return new MonitorSelector(this.monitor, false, this.fetching);
  }

  toggle(): MonitorSelector {
    if (this.fetching) {
      return this;
    }
    return new MonitorSelector(this.monitor, !this.selected, this.fetching);
  }

  updateFromFetching(monitor: Monitor): MonitorSelector {
    return new MonitorSelector(monitor, false, false);
  }

  updateFromServerEvent(monitor: Monitor): MonitorSelector {
    if (monitor.status === MonitorStatusType.Archived || this.fetching) {
      return this;
    }

    monitor = {...monitor,
      groups: this.monitor.groups,
      regions: this.monitor.regions,
      interval: this.monitor.interval
    }

    // Get the last remote date from statusSince, transientStatusSince, updatedAt
    const lastRemoteUpdatedAt = new Date(Math.max(
      new Date(monitor.statusSince).getTime(),
      new Date(monitor.transientStatusSince).getTime(),
      new Date(monitor.updatedAt).getTime()
    ));

    const lastCurrentUpdatedAt = new Date(Math.max(
      new Date(this.monitor.statusSince).getTime(),
      new Date(this.monitor.transientStatusSince).getTime(),
      new Date(this.monitor.updatedAt).getTime()
    ));

    if (lastRemoteUpdatedAt > lastCurrentUpdatedAt) {
      return new MonitorSelector(monitor, this.selected, false);
    }

    const lastRemoteRtUpdatedAt = new Date(monitor.rtUpdatedAt);
    const lastCurrentRtUpdatedAt = new Date(this.monitor.rtUpdatedAt);

    if (lastRemoteRtUpdatedAt > lastCurrentRtUpdatedAt) {
      return new MonitorSelector({
          ...this.monitor,
          rt: monitor.rt,
          rtUpdatedAt: monitor.rtUpdatedAt
        },
        this.selected, false);
    }

    return this;
  }

  public get blocked(): boolean {
    return this.fetching || this.monitor.transientStatus !== MonitorTransientStatusType.Idle;
  }

  public get isStarting(): boolean {
    return this.monitor.transientStatus === MonitorTransientStatusType.Starting;
  }

  public get isPaused(): boolean {
    return this.monitor.status === MonitorStatusType.Paused;
  }

  public get isMaintained(): boolean {
    return this.monitor.status === MonitorStatusType.Maintenance;
  }

  public get isDegraded(): boolean {
    return this.monitor.status === MonitorStatusType.Degraded;
  }

  public get isDisrupted(): boolean {
    return this.monitor.status === MonitorStatusType.Disrupted;
  }

  public get isUp(): boolean {
    return this.monitor.status === MonitorStatusType.Up;
  }

  public get isDown(): boolean {
    return this.monitor.status === MonitorStatusType.Down;
  }

  public get canPause(): boolean {
    return !this.isPaused && !this.blocked;
  }

  public get canMaintain(): boolean {
    return !this.isMaintained && !this.blocked;
  }

  public get canResume(): boolean {
    return (this.isMaintained || this.isPaused) && !this.blocked;
  }

  public get canDelete(): boolean {
    return !this.blocked;
  }
}